import { DragDropModule } from '@angular/cdk/drag-drop';
import { Overlay } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DATE_FORMATS, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import {
  MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY as MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatLegacyAutocompleteModule as MatAutocompleteModule,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { RouterModule } from '@angular/router';
import { FullCalendarModule } from '@fullcalendar/angular';
import { NgxsModule } from '@ngxs/store';
import { ProjectDialogPanelsModule } from 'app/modules/projects/project-dialog/panels/panels.module';
import { ColumnSelectorModule } from 'app/shared/components/column-selector/column-selector.module';
import { PipesModule } from 'app/shared/pipes/pipes.module';
import { SharedModule } from 'app/shared/shared.module';
import moment from 'moment';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import {
  TsBadgeModule,
  TsBreadcrumbModule,
  TsButtonModule,
  TsFilterModule,
  TSIconModule,
  TsInputComponent,
  TsOverlayModule,
  TsPageHeaderModule,
  TsPaginatorModule,
  TSTreeMenuModule,
} from 'tsui';
import { TreoAutogrowModule, TreoFindByKeyPipeModule, TreoNavigationModule } from 'tsui/@treo';
import { TsPipesModule } from 'tsui/lib/cross-cutting/pipes/ts-pipes.module';

import { LegacyTaskDetailsModule } from './details/details.module';
import { GanttPdfExportComponent } from './gantt-pdf-export/gantt-pdf-export.component';
import { LinkTasksComponent } from './link-tasks/link-tasks.component';
import { TaskListComponent } from './list/list.component';
import { LegacyTaskTableModule } from './list/table/table.module';
import { ManageTaskDependenciesComponent } from './manage-task-dependencies/manage-task-dependencies.component';
import { TasksSettingsComponent } from '../tasks/settings/settings.component';
import { LegacyTasksSidebarComponent } from './sidebar/sidebar.component';
import { TaskState } from '@tasks/state/state';
import { TasksFilterComponent } from './tasks-filter/tasks-filter.component';
import { LegacyTasksTabComponent } from './tasks.component';
import { tasksRoutes } from './tasks.routing';
import { CreateTaskListComponent } from '@tasks/task-shell/create-task-list/create-task-list.component';
import { GanttChartViewComponent } from '@tasks/task-view-gantt/gantt-chart-view/gantt-chart-view.component';

const TSUI_MODULES = [
  TSTreeMenuModule,
  TsPipesModule,
  TsPageHeaderModule,
  TsPaginatorModule,
  TsBreadcrumbModule,
  TsButtonModule,
  TSIconModule,
  TsBadgeModule,
  TsOverlayModule,
  TsFilterModule,
  TsInputComponent,
];

@NgModule({
  declarations: [
    LegacyTasksTabComponent,
    LegacyTasksSidebarComponent,
    TaskListComponent,
    TasksFilterComponent,
    ManageTaskDependenciesComponent,
    LinkTasksComponent,
    GanttPdfExportComponent,
  ],
  imports: [
    CommonModule,
    RouterModule.forChild(tasksRoutes),
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatButtonToggleModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatMomentDateModule,
    MatProgressBarModule,
    NgxMaterialTimepickerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatTableModule,
    MatTooltipModule,
    TreoAutogrowModule,
    TreoFindByKeyPipeModule,
    TreoNavigationModule,
    DragDropModule,
    MatAutocompleteModule,
    MatChipsModule,
    SharedModule,
    FullCalendarModule,
    ProjectDialogPanelsModule,
    ColumnSelectorModule,
    MatBadgeModule,
    PipesModule,
    MatSlideToggleModule,
    LegacyTaskTableModule,
    LegacyTaskDetailsModule,
    GanttChartViewComponent,
    CreateTaskListComponent,
    NgxsModule.forRoot([TaskState]),
    TasksSettingsComponent,
    ...TSUI_MODULES,
  ],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: moment.ISO_8601,
        },
        display: {
          dateInput: 'LL',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: (overlay: Overlay) => {
        return () => overlay.scrollStrategies.block();
      },
      deps: [Overlay],
    },
  ],
})
export class LegacyTasksTabModule {}
